import React, { useState } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Menu, Search, Bell, ChevronRight } from "lucide-react";
import { pageLabel } from "./navConfig";
import CommandMenu from "./CommandMenu";
import { useAuth } from "@/lib/AuthContext";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const NOTIFICATIONS = [
  { title: "3 fakturaer nærmer seg forfall", meta: "Faktura · i dag", tone: "amber" },
  { title: "Signeringsmøte med Vestland Ventures", meta: "Oppfølging · om 3 dager", tone: "lime" },
  { title: "Due diligence TechFlow oppdatert", meta: "Prosjekt · i går", tone: "slate" },
];

export default function Topbar({ currentPageName, onMenuClick }) {
  const [cmdOpen, setCmdOpen] = useState(false);
  const { user } = useAuth();
  const isExternal = user?.role === "external";
  const name = user?.full_name || "Bruker";
  const initials = name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-20 h-16 flex items-center gap-3 px-4 lg:px-8 bg-white/80 backdrop-blur-md border-b border-slate-200/70">
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 -ml-1 rounded-lg text-slate-600 hover:bg-slate-100"
        aria-label="Åpne meny"
      >
        <Menu className="w-5 h-5" />
      </button>

      <div className="hidden lg:flex items-center gap-1.5 text-sm">
        <span className="text-slate-400">Murphy AS</span>
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <span className="font-semibold text-brand-ink">{pageLabel(currentPageName)}</span>
      </div>

      <div className="flex-1" />

      {!isExternal && (
        <button
          onClick={() => setCmdOpen(true)}
          className="group flex items-center gap-2 h-9 pl-3 pr-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 transition-colors text-sm text-slate-400"
        >
          <Search className="w-4 h-4" />
          <span className="hidden sm:inline">Søk...</span>
          <kbd className="hidden sm:inline-flex items-center gap-0.5 ml-2 px-1.5 py-0.5 rounded-md border border-slate-200 bg-white text-[10px] font-semibold text-slate-500">
            ⌘K
          </kbd>
        </button>
      )}

      {!isExternal && (
      <Popover>
        <PopoverTrigger className="relative p-2 rounded-xl text-slate-500 hover:text-brand-ink hover:bg-slate-100 transition-colors outline-none">
          <Bell className="w-5 h-5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-brand-limeDark ring-2 ring-white" />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="font-semibold text-brand-ink text-sm">Varsler</span>
            <span className="text-xs text-brand-limeDark font-semibold">{NOTIFICATIONS.length} nye</span>
          </div>
          <div className="divide-y divide-slate-100">
            {NOTIFICATIONS.map((n, i) => (
              <div key={i} className="flex gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                <span
                  className={
                    "mt-1 w-2 h-2 rounded-full shrink-0 " +
                    (n.tone === "amber" ? "bg-amber-400" : n.tone === "lime" ? "bg-brand-limeDark" : "bg-slate-300")
                  }
                />
                <div className="min-w-0">
                  <p className="text-sm text-brand-ink leading-snug">{n.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{n.meta}</p>
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      )}

      <Link
        to={createPageUrl(isExternal ? "Deals" : "UserSettings")}
        className="w-9 h-9 rounded-full bg-brand-ink text-brand-lime hidden sm:flex items-center justify-center text-xs font-bold hover:opacity-90 transition-opacity"
        title={name}
      >
        {initials}
      </Link>

      {!isExternal && <CommandMenu open={cmdOpen} setOpen={setCmdOpen} />}
    </header>
  );
}
